import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@quantiix/design-system';
import { useTranslation } from '../../../i18n';
import { useToast } from '../../../contexts/ToastContext';
import { createLogger } from '../../../utils/logger';
import {
    NOTIFICATION_CHANNELS,
    PHONE_CHANNELS,
    toChannelState,
    toNotificationMethods,
    channelsDiffer
} from '../utils/notificationChannels';
import shared from '../Settings.shared.module.css';
import styles from './EventsSection.module.css';

const logger = createLogger('Shell::Settings::Events');

function EventsSection({ settings, editUser }) {
    const { t } = useTranslation('shell');
    const { addToast } = useToast();

    // What the backend currently holds. The checkboxes start here and revert
    // here, so a reload shows what was saved rather than the defaults - these
    // toggles used to be local-only state that never reached the server.
    const saved = useMemo(
        () => toChannelState(settings.notificationMethods),
        [settings.notificationMethods]
    );

    const [channels, setChannels] = useState(saved);
    const [isSaving, setIsSaving] = useState(false);

    // Re-sync after each successful save (useUserSettings refetches) and on load.
    useEffect(() => { setChannels(saved); }, [saved]);

    const toggleChannel = (id) => {
        setChannels(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const isDirty = channelsDiffer(channels, saved);

    // SMS and WhatsApp are delivered to the profile phone; without one they are
    // switched on but silent, so say so instead of letting it fail quietly.
    const needsPhone = PHONE_CHANNELS.some(id => channels[id]);
    const phoneMissing = needsPhone && !settings.phone;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await editUser({
                email: settings.email,
                notification_methods: toNotificationMethods(channels, settings.notificationMethods)
            });
            logger.info('Notification channels saved', channels);
            addToast({
                type: 'success',
                message: t('settings.events.notificationTypes.saveSuccess'),
                duration: 3000
            });
        } catch (err) {
            logger.error('Failed to update notification channels', err);
            addToast({
                type: 'error',
                message: err?.message || t('settings.events.notificationTypes.saveError'),
                duration: 4000
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => setChannels(saved);

    const handleAddSchedule = () => {
        // TODO: open the Do-Not-Disturb schedule editor modal once design lands.
        // Until then, surface a toast so we can wire the click without leaving dead UI.
        logger.info('Add schedule clicked');
        addToast({
            type: 'info',
            message: t('settings.events.dnd.addScheduleStub'),
            duration: 3000
        });
    };

    return (
        <div className={shared.sectionContent}>
            <div className={shared.block}>
                <h3 className={shared.sectionTitle}>{t('settings.events.title')}</h3>
                <p className={shared.description}>{t('settings.events.description')}</p>
            </div>

            <div className={shared.block}>
                <h3 className={shared.sectionTitle}>{t('settings.events.notificationTypes.title')}</h3>
                <p className={shared.description}>{t('settings.events.notificationTypes.description')}</p>

                <div className={styles.checkboxGroupLabel}>
                    {t('settings.events.notificationTypes.label')}
                </div>
                <ul className={styles.checkboxList}>
                    {NOTIFICATION_CHANNELS.map(channel => (
                        <li key={channel.id} className={styles.checkboxItem}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={channels[channel.id]}
                                    onChange={() => toggleChannel(channel.id)}
                                />
                                <span>{t(channel.labelKey)}</span>
                            </label>
                        </li>
                    ))}
                </ul>

                <p className={phoneMissing ? styles.phoneWarning : shared.helperText}>
                    {phoneMissing
                        ? t('settings.events.notificationTypes.phoneMissing')
                        : t('settings.events.notificationTypes.phoneHint')}
                </p>

                <div className={shared.actions}>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        isDisabled={!isDirty || isSaving}
                    >
                        {t('settings.events.notificationTypes.saveChanges')}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                        isDisabled={!isDirty || isSaving}
                    >
                        {t('settings.events.notificationTypes.cancel')}
                    </Button>
                </div>
            </div>

            <div className={shared.block}>
                <h3 className={shared.sectionTitle}>{t('settings.events.dnd.title')}</h3>
                <p className={shared.description}>{t('settings.events.dnd.description')}</p>

                <Button variant="primary" onClick={handleAddSchedule}>
                    {t('settings.events.dnd.addSchedule')}
                </Button>
            </div>
        </div>
    );
}

export default EventsSection;
